import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { MapaDoBrasilSvg } from "./MapaUi";
import {
  INTEGRADORES,
  rotuloDoIntegrador,
  type EstadoDaLegalizacao,
  type Integrador,
} from "../lib/legaliza";

// As peças do Legaliza Fácil. Só apresentação: nenhuma decide acesso, nenhuma
// busca dado, nenhuma monta caminho de arquivo.
//
// ---------------------------------------------------------------------------
// As quatro cores saem de TOKEN, e o território redefine as variáveis
// ---------------------------------------------------------------------------
// `bg-integrador-*` é semântico e troca com o tema — a cicatriz das três cores de
// setor do Calendário, medida de novo para estas quatro em 25/08: nenhuma cor de
// marca passa 3:1 nos dois fundos (`azul-medio` dá 10,76 no branco e 1,45 no
// escuro). E como o mapa mora dentro de `painel-navy`, que é escuro nos DOIS
// temas, é o próprio território que redefine as variáveis no `index.css` — por
// isso nenhum componente daqui precisa saber onde está.

const CLASSE_DO_INTEGRADOR: Record<Integrador, string> = {
  rede_simples: "bg-integrador-rede-simples",
  regin: "bg-integrador-regin",
  empresa_facil: "bg-integrador-empresa-facil",
  vre: "bg-integrador-vre",
};

const COR_DO_INTEGRADOR: Record<Integrador, string> = {
  rede_simples: "var(--cf-integrador-rede-simples)",
  regin: "var(--cf-integrador-regin)",
  empresa_facil: "var(--cf-integrador-empresa-facil)",
  vre: "var(--cf-integrador-vre)",
};

/**
 * O mapa das 27 juntas — um invólucro de `MapaDoBrasilSvg`.
 *
 * O DESENHO saiu daqui em 27/08/2026, quando o Mapa de Concursos passou a
 * precisar do mesmo mapa com uma escala QUANTITATIVA. Copiar o componente
 * duplicaria três coisas que foram pagas com medição — a tabela dos oito estados
 * cuja sigla não cabe no contorno, o `import()` que mantém os 131 kB de
 * geometria fora do bundle principal, e o `aria-hidden` com a razão escrita.
 * Ficou em `componentes/MapaUi.tsx`; aqui fica só o que é do Legaliza: a cor por
 * INTEGRADOR e o título de cada estado.
 *
 * O DOM não mudou, e isso é medido: `app/tests/legaliza.mjs` conta 27
 * `[data-mapa] path[data-uf]`, 8 `[data-mapa] line`, cobra o `aria-hidden` e mede
 * o contraste de `path[data-uf="XX"]` contra `.painel-navy`.
 */
export function MapaDoBrasil({
  estados,
  ativo,
  onEscolher,
}: {
  estados: EstadoDaLegalizacao[];
  ativo?: string | null;
  onEscolher: (uf: string) => void;
}) {
  const porUf = new Map(estados.map((e) => [e.uf, e]));
  return (
    <MapaDoBrasilSvg
      ufs={estados.map((e) => e.uf)}
      corDaUf={(uf) => COR_DO_INTEGRADOR[porUf.get(uf)!.integrador]}
      tituloDaUf={(uf) => {
        const e = porUf.get(uf)!;
        return `${e.nome} — ${rotuloDoIntegrador(e.integrador)}`;
      }}
      ativo={ativo}
      onEscolher={onEscolher}
      corDoTraco="#0c1f40"
    />
  );
}

/**
 * A legenda dos quatro integradores.
 *
 * Ela explica o que cada um É, e não só o nome. "REGIN" não diz nada a quem
 * abre a ferramenta pela primeira vez, e a diferença entre os quatro é a coisa
 * mais útil desta tela: no REGIN a abertura é na junta e a alteração é no coletor
 * da Receita, e é por isso que os três botões apontam para lugares diferentes.
 */
export function LegendaDeIntegradores({ contagem }: { contagem: Record<Integrador, number> }) {
  return (
    <ul className="space-y-2.5">
      {INTEGRADORES.map((i) => (
        <li key={i.valor} className="flex gap-2.5">
          <span
            className={`mt-1.5 size-2.5 shrink-0 rounded-full ${CLASSE_DO_INTEGRADOR[i.valor]}`}
            aria-hidden="true"
          />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white">
              {i.rotulo}{" "}
              <span className="font-normal text-navy-mute">
                · {contagem[i.valor] ?? 0} {contagem[i.valor] === 1 ? "estado" : "estados"}
              </span>
            </p>
            <p className="text-xs leading-relaxed text-navy-mute">{i.explica}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * O ponto colorido do integrador.
 *
 * Cor sozinha nunca carrega informação: o ponto é `aria-hidden` e o nome vem
 * escrito ao lado, sempre. É a mesma regra do `PontoDeSetor` do Calendário.
 *
 * Ele funciona DENTRO e FORA do navy sem receber prop nenhuma, porque quem troca
 * a cor é a variável que o território redefine — não este componente.
 */
export function PontoDeIntegrador({ integrador }: { integrador: Integrador }) {
  return (
    <span
      className={`inline-block size-2.5 shrink-0 rounded-full ${CLASSE_DO_INTEGRADOR[integrador]}`}
      aria-hidden="true"
    />
  );
}

/** O selo com o nome — para as telas de administração, fora do navy. */
export function SeloDeIntegrador({ integrador }: { integrador: Integrador }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-chip px-2.5 py-1 text-xs font-semibold text-chip-tinta">
      <PontoDeIntegrador integrador={integrador} />
      {rotuloDoIntegrador(integrador)}
    </span>
  );
}

/**
 * A lista dos 27, e ela não é o "plano B" do mapa.
 *
 * É o caminho do teclado e do leitor de tela (o mapa é `aria-hidden`), é o único
 * caminho utilizável no celular — onde Sergipe tem 4 mm — e é o mais rápido para
 * quem já sabe o estado que quer. A cicatriz do IR Pro diz isso com todas as
 * letras: lista densa de texto encontra melhor que grade, porque o uso real é
 * procurar pelo nome.
 */
export function ListaDeEstados({
  estados,
  ativo,
  base,
}: {
  estados: EstadoDaLegalizacao[];
  ativo?: string | null;
  base: string;
}) {
  return (
    // `columns-2`, e não `grid-cols-2`: numa GRADE a ordem alfabética anda em
    // zigue-zague (Acre à esquerda, Alagoas à direita, Amapá de volta à
    // esquerda) e o olho perde a sequência. Em COLUNAS a primeira metade do
    // alfabeto fica de um lado e a segunda do outro, que é como se lê uma lista
    // de 27 nomes.
    <ul className="gap-x-6 sm:columns-2">
      {estados.map((e) => (
        <li key={e.uf} className="break-inside-avoid">
          <Link
            to={`${base}/${e.uf}`}
            className={`flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition hover:bg-tile focus-visible:bg-tile focus-visible:outline-none ${
              ativo === e.uf ? "bg-tile font-semibold text-titulo" : "text-tinta"
            }`}
          >
            <span
              className={`size-2 shrink-0 rounded-full ${CLASSE_DO_INTEGRADOR[e.integrador]}`}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate">{e.nome}</span>
            <span className="shrink-0 font-mono text-xs text-cinza">{e.uf}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** Um bloco de conteúdo da tela do estado. */
export function Bloco({
  titulo,
  descricao,
  children,
  acao,
}: {
  titulo: string;
  descricao?: ReactNode;
  children: ReactNode;
  acao?: ReactNode;
}) {
  const id = `bloco-${titulo.toLowerCase().replace(/[^a-z]+/g, "-")}`;
  return (
    // `<section role="group" aria-labelledby>` e não `<fieldset><legend>`: a
    // especificação posiciona o legend SOBRE a borda, e com o card arredondado o
    // título sai flutuando fora da caixa. Visto na tela em 19/08.
    <section role="group" aria-labelledby={id} className="surface p-5 sm:p-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 id={id} className="font-display text-lg font-bold text-titulo">
            {titulo}
          </h3>
          {descricao && <div className="mt-1 text-sm leading-relaxed text-cinza">{descricao}</div>}
        </div>
        {acao}
      </div>
      {children}
    </section>
  );
}

/** O aviso de responsabilidade, na cor de alerta da casa. */
export function Alerta({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-cf border border-alerta-tinta/30 bg-alerta-fundo p-4 text-sm leading-relaxed text-tinta">
      {children}
    </div>
  );
}
